import { CancelTokenSource } from 'axios';
import { useRef, useState } from 'react';

import { AuthToken } from '@services/auth-token/authToken';
import { generateNewCancelTokenSource } from '@services/axios/axiosUtils';
import { BackOfficeAPIClient } from '@services/axios/clients/backOfficeAPIClient';
import { neitherNullNorUndefined } from '@services/validation/validation';
import { UpdateOrderStatusPayload } from '@shared/interfaces/backoffice';

export default function usePaymentRegistry() {
    const [paymentState, setPaymentState] = useState(null);
    const [statusState, setStatusState]   = useState(null);
    const [upgradeState, setUpgradeState] = useState(null);
    const [isLoading, setIsLoading]       = useState(false);
    const [hasError, setHasError]         = useState(false);

    const tokenSource        = useRef<CancelTokenSource>(null);
    const statusTokenSource  = useRef<CancelTokenSource>(null);
    const upgradeTokenSource = useRef<CancelTokenSource>(null);

    const initPaymentRegistry = async ({
        paymentId, planId, orderId, endDate, startDate, annualPayment = false
    }: {
        paymentId: string;
        planId: number;
        orderId?: number;
        startDate?: string;
        endDate?: string;
        annualPayment?: boolean;
    }) => {
        setIsLoading(true);

        if (tokenSource.current) {
            tokenSource.current.cancel();
        }

        tokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response     = await BackOfficeAPIClient.addOrUpdateOrder({
                ...(neitherNullNorUndefined(orderId) ? { orderId } : {}),
                ...(neitherNullNorUndefined(startDate) ? { startDate } : {}),
                ...(neitherNullNorUndefined(endDate) ? { endDate } : {}),
                paymentId,
                clientId,
                planId,
                annualPayment,
                autoRenew: true,
            }, {
                cancelToken: tokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setPaymentState(response.data);
                setHasError(false);
                setIsLoading(false);

                return response.data;
            }

            throw new Error('Failed to add pending payment!');
        } catch (e) {
            console.error(e);
            setHasError(true);
            setIsLoading(false);

            return null;
        }
    };

    const updatePaymentStatus = async (orderId: string, status: UpdateOrderStatusPayload['status']) => {
        setIsLoading(true);

        if (statusTokenSource.current) {
            statusTokenSource.current.cancel();
        }

        statusTokenSource.current = generateNewCancelTokenSource();

        try {
            const response = await BackOfficeAPIClient.updateOrderStatus(orderId, {
                status
            }, {
                cancelToken: statusTokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setStatusState(response.data);
                setHasError(false);
                setIsLoading(false);

                return response.data;
            }

            throw new Error('Failed to update order status!');
        } catch (e) {
            console.error(e);
            setHasError(true);
            setIsLoading(false);

            return null;
        }
    };

    const upgradePaymentPlan = async (newPlanId: number) => {
        if (upgradeTokenSource.current) {
            upgradeTokenSource.current.cancel();
        }

        upgradeTokenSource.current = generateNewCancelTokenSource();

        try {
            const token        = AuthToken.getToken();
            const decodedToken = new AuthToken(token).getDecodedToken;
            const clientId     = decodedToken?.site?.name;

            if (!clientId) {
                throw new Error('Client ID is missing from token!');
            }

            const response     = await BackOfficeAPIClient.upgradePlan(clientId, newPlanId, {
                cancelToken: upgradeTokenSource.current.token,
            });

            if ([200, 201].includes(response.status)) {
                setUpgradeState(response.data);
                setHasError(false);
                setIsLoading(false);

                return response.data;
            }

            throw new Error('Failed to add upgrade plan!');
        } catch (e) {
            console.error(e);
            setHasError(true);
            setIsLoading(false);

            return null;
        }
    };

    return {
        hasError,
        isLoading,

        paymentState,
        initPaymentRegistry,

        statusState,
        updatePaymentStatus,

        upgradeState,
        upgradePaymentPlan,
    };
}
